import {
  Component,
  ChangeDetectionStrategy,
  Input,
  Output,
  EventEmitter,
} from '@angular/core';
import { SPINNER_ICON, STAR_ICON, STAR_ICON_FILL } from 'app/constants';

@Component({
  selector: 'app-star-toggle',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <span class="follow-actions">
      <button
        *ngIf="showFollowButton && !ignored && (!fullButton || !active)"
        pButton
        type="button"
        [icon]="currentIcon"
        [attr.aria-label]="active ? 'Followed' : 'Follow'"
        [pTooltip]="active ? 'Unfollow' : 'Follow'"
        [disabled]="isLoading"
        [label]="fullButton ? 'Follow' : null"
        [class.header-action]="fullButton"
        [class.p-button-outlined]="fullButton"
        [class.p-button-text]="!fullButton"
        class="p-button-secondary follow-icon"
        (click)="handleStarOnClick($event)"
      ></button>
      <button
        *ngIf="showFollowButton && fullButton && active && !ignored"
        pButton
        type="button"
        [icon]="currentIcon"
        aria-label="Followed"
        pTooltip="Unfollow"
        [disabled]="isLoading"
        label="Followed"
        class="p-button-secondary header-action header-action-active follow-icon"
        (click)="handleStarOnClick($event)"
      ></button>
      <button
        *ngIf="allowIgnore && (!active || ignored) && (!fullButton || !ignored)"
        pButton
        type="button"
        [icon]="isLoading ? spinnerIcon : ignored ? ignoredIcon : ignoreIcon"
        [attr.aria-label]="ignored ? 'Ignored' : 'Ignore'"
        [pTooltip]="ignored ? 'Unignore' : 'Ignore'"
        [disabled]="isLoading"
        [label]="fullButton ? 'Ignore' : null"
        [class.header-action]="fullButton"
        [class.p-button-outlined]="fullButton"
        [class.p-button-text]="!fullButton"
        class="p-button-secondary follow-icon"
        (click)="handleIgnoreOnClick($event)"
      ></button>
      <button
        *ngIf="allowIgnore && fullButton && ignored"
        pButton
        type="button"
        [icon]="isLoading ? spinnerIcon : ignoredIcon"
        aria-label="Ignored"
        pTooltip="Unignore"
        [disabled]="isLoading"
        label="Ignored"
        class="p-button-secondary header-action header-action-active follow-icon"
        (click)="handleIgnoreOnClick($event)"
      ></button>
      <button
        *ngFor="let label of shortcutLabels"
        pButton
        type="button"
        [label]="label"
        [disabled]="isLoading"
        [class.p-button-outlined]="activeLabel !== label"
        [class.p-button-secondary]="activeLabel !== label"
        class="p-button-sm label-shortcut"
        (click)="handleLabelOnClick($event, label)"
      ></button>
    </span>
  `,
  styles: [
    `
      :host .follow-actions {
        display: inline-flex;
        align-items: center;
        gap: 0.35rem;
        font-family: 'Nunito Sans', sans-serif;
        white-space: nowrap;
      }

      :host .follow-actions .p-button {
        font-family: inherit;
      }

      :host .p-button:not(.header-action):not(.label-shortcut) {
        border-color: transparent !important;
        box-shadow: none !important;
        cursor: pointer;
      }

      :host .p-button:not(.header-action):not(.label-shortcut):hover {
        background: none !important;
        border-color: transparent !important;
      }

      :host .p-button:focus {
        box-shadow: none !important;
      }

      :host .label-shortcut {
        height: 1.6rem;
        padding: 0.2rem 0.45rem;
        color: #465675 !important;
        background: #fff !important;
        border: 1px solid #8793a6 !important;
        border-radius: 5px !important;
        font-size: 0.72rem;
      }

      :host .label-shortcut:not(.p-button-outlined) {
        color: #fff !important;
        background: #465675 !important;
        border-color: #465675 !important;
      }

      :host .header-action {
        height: 1.75rem;
        padding: 0.25rem 0.5rem;
        border-radius: 6px !important;
        font-size: 0.78rem;
        line-height: 1;
      }

      :host .header-action .p-button-icon {
        font-size: 0.75rem;
      }

      :host
        .p-button.header-action.header-action-active.p-button-secondary,
      :host
        .p-button.header-action.header-action-active.p-button-secondary.p-button-outlined {
        color: #fff !important;
        background: #465675 !important;
        border-color: #465675 !important;
      }

      :host
        .p-button.header-action.header-action-active.p-button-secondary:hover {
        background: #3a4a63 !important;
        border-color: #3a4a63 !important;
      }
    `,
  ],
})
export class StarToggleComponent {
  @Input() active = false;
  @Input() ignored = false;
  @Input() allowIgnore = false;
  @Input() activeLabel: string = null;
  @Input() labels: string[] = [];
  @Input() fullButton = false;
  @Input() showFollowButton = true;
  @Input() isLoading = false;
  @Output() toggled = new EventEmitter<boolean>();
  @Output() ignoreToggled = new EventEmitter<boolean>();
  @Output() labelToggled = new EventEmitter<string>();

  public readonly spinnerIcon = SPINNER_ICON;
  public readonly ignoreIcon = 'fas fa-ban';
  public readonly ignoredIcon = 'fas fa-ban';

  get shortcutLabels(): string[] {
    return this.active && !this.ignored ? this.labels : [];
  }

  get currentIcon(): string {
    if (this.isLoading) {
      return SPINNER_ICON;
    }
    return this.active ? STAR_ICON_FILL : STAR_ICON;
  }

  handleStarOnClick(event: Event): void {
    event.stopPropagation();
    this.toggled.emit(this.active);
  }

  handleIgnoreOnClick(event: Event): void {
    event.stopPropagation();
    this.ignoreToggled.emit(this.ignored);
  }

  handleLabelOnClick(event: Event, label: string): void {
    event.stopPropagation();
    this.labelToggled.emit(label);
  }
}
